import React, { FC, useCallback, useMemo } from 'react';
import Badge from '@atlaskit/badge';
import Lozenge from '@atlaskit/lozenge';
import Toggle from '@atlaskit/toggle';
import { useLocation } from 'react-router-dom';
import qs from 'query-string';
import { useQueryParams } from 'use-query-params';
import {
  SloGroupCohortSummaryDTO,
  SloGroupSummaryDTO,
  SloQualityResultDTO,
} from '@atlassiansox/phobos-client-ts';

import {
  flexStart,
  flexSpace,
  spaceGapCompact,
  spaceGapMinimum,
  spaceTopComfortable,
} from '@/styles';
import { KeyValue, BreachCount } from '@/components/primitive';
import { QualityScoreIndicator, isQualityAvailable } from '@/components/composed';

import { formatPerformanceResult } from '../slo-card/utils';

import * as styles from './styles';

export type SloGroupSummaryProps = {
  sloGroupSummary?: SloGroupSummaryDTO;
  quality?: SloQualityResultDTO;
};

const Summary: FC<SloGroupSummaryProps> = ({ sloGroupSummary, quality }) => {
  const { search } = useLocation();
  const { ignoreSummaryDrafts } = useMemo(
    () => ({
      ignoreSummaryDrafts: qs.parse(search).hideDrafts,
    }),
    [search]
  );

  const [, setQuery] = useQueryParams({});
  const handleIgnoreSummaryDraftsFilterChange = useCallback(() => {
    setQuery({
      hideDrafts: ignoreSummaryDrafts ? undefined : true,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ignoreSummaryDrafts]);

  const hasNoCohortSummaries: boolean =
    !sloGroupSummary ||
    !sloGroupSummary.sloGroupCohortSummaries ||
    !sloGroupSummary.sloGroupCohortSummaries.length;

  const { total, breaches } = useMemo(() => {
    const sloGroupCohortSummaries: SloGroupCohortSummaryDTO[] =
      sloGroupSummary?.sloGroupCohortSummaries || [];
    const totalCount: number = sloGroupCohortSummaries.reduce(
      (acc: number, x: SloGroupCohortSummaryDTO) =>
        acc + (ignoreSummaryDrafts && x.draft ? 0 : x?.count || 0),
      0
    );
    const breachesCount: number = sloGroupCohortSummaries.reduce(
      (acc: number, x: SloGroupCohortSummaryDTO) =>
        acc + (ignoreSummaryDrafts && x.draft ? 0 : x?.breachesCount || 0),
      0
    );

    return { total: totalCount, breaches: breachesCount };
  }, [sloGroupSummary, ignoreSummaryDrafts]);

  if (hasNoCohortSummaries) {
    return null;
  }

  const hasSLOs: boolean = total > 0;

  return (
    <div css={[styles.generalBarStyles, styles.summaryBarStyles, spaceTopComfortable, flexSpace]}>
      <div css={[flexStart, spaceGapCompact]}>
        <Badge>
          <span css={styles.badgeStyles}>SLO</span>
        </Badge>
        <KeyValue noPositionFix title="breaching">
          <BreachCount count={breaches} />
        </KeyValue>
        <img alt="/" width={9} height={9} src="/assets/slash.svg" />
        <KeyValue noPositionFix title="total">
          <Badge max={Infinity} appearance="default">
            {total}
          </Badge>
        </KeyValue>
      </div>
      <KeyValue noPositionFix title="% of breaching">
        {/* eslint-disable-next-line no-nested-ternary */}
        <Lozenge appearance={breaches > 0 ? 'removed' : hasSLOs ? 'success' : 'default'}>
          {formatPerformanceResult(hasSLOs ? breaches / total : undefined)}
          {hasSLOs ? ' %' : ''}
        </Lozenge>
      </KeyValue>
      {isQualityAvailable(quality) && (
        <KeyValue noPositionFix title="quality score">
          <QualityScoreIndicator quality={quality} />
        </KeyValue>
      )}
      <div css={[flexStart, spaceGapMinimum]}>
        <span>
          Ignore <Lozenge appearance="moved">🛠 Draft</Lozenge>
        </span>
        <Toggle
          onChange={handleIgnoreSummaryDraftsFilterChange}
          isChecked={ignoreSummaryDrafts === 'true'}
        />
      </div>
    </div>
  );
};

export default Summary;
